import Highcharts, { type Options, type Point } from 'highcharts';
import { HighchartsReact, type HighchartsReactRefObject } from 'highcharts-react-official';
import { type Ref, useLayoutEffect } from 'react';

import useForwardedRef from '@/hooks/useForwardedRef';

import { type ChartCallbackFunction } from './types';

type Props = {
  ref?: Ref<HighchartsReactRefObject>;
  callback?: ChartCallbackFunction;
  options: Options | null | undefined;
  measuredWidth?: number;
  measuredHeight?: number;
  disabledSeriesIds?: string[];
};

type PointExtended = Point & {
  id?: string;
};

const HighchartsRenderer = ({
  ref,
  callback,
  options,
  measuredWidth,
  measuredHeight,
  disabledSeriesIds,
}: Props) => {
  const chartRef = useForwardedRef<HighchartsReactRefObject>(ref);

  useLayoutEffect(() => {
    const chart = chartRef.current?.chart;
    if (chart && measuredWidth && measuredHeight) {
      chart.setSize(measuredWidth, measuredHeight, false);
    }
  }, [measuredWidth, measuredHeight, chartRef]);

  useLayoutEffect(() => {
    const chart = chartRef.current?.chart;
    if (chart && disabledSeriesIds) {
      chart.series.forEach((series) => {
        // Each pie slice is typically seen as a series, so we toggle them on/off instead.
        if (series.type === 'pie') {
          series.points.forEach((point) => {
            const id = (point as PointExtended).id || '';
            point.setVisible(disabledSeriesIds?.includes(id) === false);
          });
        } else {
          series.setVisible(disabledSeriesIds?.includes(series.options.id ?? '') === false);
        }
      });
    }
  }, [disabledSeriesIds, chartRef]);

  return (
    <HighchartsReact
      ref={chartRef}
      highcharts={Highcharts}
      containerProps={{
        style: {
          width: '100%',
          height: '100%',
        },
      }}
      options={options}
      callback={callback}
    />
  );
};

export default HighchartsRenderer;
